<template>
  <DefaultField
    :field="currentField"
    :errors="errors"
    :show-help-text="showHelpText"
    :full-width-content="true"
  >
    <template #field>
      <div class="pct-root">
        <div class="pct-list-scroll">
          <div class="pct-list">
          <article
            v-for="row in activeRows"
            :key="row.course_id"
            class="pct-item"
          >
            <div class="pct-item-main">
              <div class="pct-media">
                <img
                  v-if="row.cover_image"
                  :src="row.cover_image"
                  :alt="row.course_title"
                  class="pct-cover"
                />
                <div v-else class="pct-cover pct-cover-fallback">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4.5 3.75a3 3 0 00-3 3v.75h21v-.75a3 3 0 00-3-3h-15z" />
                    <path fill-rule="evenodd" d="M22.5 9.75h-21v7.5a3 3 0 003 3h15a3 3 0 003-3v-7.5zm-11.25-3a.75.75 0 01.75.75v.75a.75.75 0 01-1.5 0V7.5a.75.75 0 01.75-.75zm3 0a.75.75 0 01.75.75v.75a.75.75 0 01-1.5 0V7.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>

              <div class="pct-info">
                <h4 class="pct-title" :title="row.course_title">{{ row.course_title }}</h4>
                <div class="pct-institute" :title="row.institute_name">
                  <img
                    v-if="row.institute_logo"
                    :src="row.institute_logo"
                    :alt="row.institute_name"
                    class="pct-institute-logo"
                  />
                  <span v-else class="pct-institute-dot"></span>
                  <span class="pct-institute-name">{{ row.institute_name }}</span>
                </div>
              </div>

              <button
                type="button"
                class="pct-remove"
                :disabled="! canRemoveRow(row.course_id)"
                :title="canRemoveRow(row.course_id) ? __('Remove course') : __('At least two courses are required')"
                @mousedown.stop
                @click.stop.prevent="removeRow(row.course_id)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 9.24A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-9.24.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <div class="pct-item-fields">
              <div class="pct-field">
                <span class="pct-field-label">{{ __('Catalog Price') }}</span>
                <span class="pct-field-value">{{ formatMoney(row.catalog_price) }}</span>
              </div>
              <div class="pct-field">
                <span class="pct-field-label">{{ __('Package Course Price') }}</span>
                <div class="pct-price-field">
                  <input
                    type="text"
                    inputmode="numeric"
                    class="pct-input"
                    :value="formattedPriceInput(row.course_id)"
                    @input="updatePriceFromInput(row.course_id, $event)"
                  />
                  <span class="pct-input-suffix">{{ __('Toman') }}</span>
                </div>
              </div>
              <div class="pct-field pct-field-sm">
                <span class="pct-field-label">{{ __('Commission Percent') }}</span>
                <div class="pct-commission-field">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    class="pct-input pct-input-sm"
                    :disabled="isCommissionDisabled(row.course_id)"
                    :value="lineValue(row.course_id, 'commission_percent')"
                    @input="updateCommission(row.course_id, $event.target.value)"
                    @blur="clampCommission(row.course_id)"
                  />
                  <span class="pct-percent-sign">%</span>
                </div>
              </div>
            </div>
          </article>

          <div v-if="activeRows.length === 0" class="pct-empty">
            {{ __('No courses available for this package.') }}
          </div>
          </div>
        </div>

        <div class="pct-summary" :class="{ 'pct-summary-over': isOverMaxPrice }">
          <div class="pct-summary-grid">
            <div class="pct-summary-item">
              <span class="pct-summary-label">{{ __('Final Package Price') }}</span>
              <span class="pct-summary-value">{{ formatPrice(totalPrice) }}</span>
            </div>
            <div class="pct-summary-item">
              <span class="pct-summary-label">{{ __('Maximum allowed price') }}</span>
              <span class="pct-summary-value pct-summary-value-muted">{{ formatPrice(maxPackagePrice) }}</span>
            </div>
          </div>
          <div v-if="isOverMaxPrice" class="pct-summary-error">
            {{ __('Package total exceeds the allowed limit of :max.', { max: formatPrice(maxPackagePrice) }) }}
          </div>
        </div>
      </div>
    </template>
  </DefaultField>
</template>

<script>
import { DependentFormField, HandlesValidationErrors } from '@/mixins'
import numeral from 'numeral'

export default {
  mixins: [DependentFormField, HandlesValidationErrors],

  data: () => ({
    lines: {},
    removedIds: [],
    cachedRows: [],
    stateReady: false,
  }),

  computed: {
    rows() {
      const current = (this.currentField.rows || []).map(row => this.normalizeRow(row))

      if (current.length > 0) {
        return current
      }

      return this.cachedRows
    },

    maxPackagePrice() {
      return Number(this.currentField.maxPackagePrice ?? this.field.maxPackagePrice ?? 10_000_000)
    },

    activeRows() {
      return this.rows.filter(row => ! this.isRemoved(row.course_id))
    },

    totalPrice() {
      return this.activeRows.reduce((sum, row) => {
        return sum + this.numericPrice(row.course_id)
      }, 0)
    },

    isOverMaxPrice() {
      return this.totalPrice > this.maxPackagePrice
    },
  },

  mounted() {
    this.cacheRows()
    this.bootstrapState()
  },

  methods: {
    cacheRows() {
      const initial = (this.field.rows || []).map(row => this.normalizeRow(row))

      if (initial.length > 0) {
        this.cachedRows = initial
      }
    },

    normalizeRow(row) {
      return {
        course_id: Number(row.course_id),
        course_title: row.course_title ?? row.title ?? '-',
        cover_image: row.cover_image ?? null,
        institute_name: row.institute_name ?? row.institute ?? '-',
        institute_logo: row.institute_logo ?? null,
        catalog_price: Number(row.catalog_price ?? row.course_price ?? 0),
        package_price: Number(row.package_price ?? row.course_price ?? 0),
        commission_percent: Number(row.commission_percent ?? 0),
      }
    },

    isRemoved(courseId) {
      const id = Number(courseId)

      return this.removedIds.some(removedId => Number(removedId) === id)
    },

    canRemoveRow(courseId) {
      if (this.isRemoved(courseId)) {
        return false
      }

      return this.activeRows.length > 2
    },

    bootstrapState() {
      const existing = this.parseValue(this.value)
      const next = {}

      this.rows.forEach(row => {
        const saved = existing[row.course_id] ?? existing[String(row.course_id)] ?? {}

        next[row.course_id] = {
          price: saved.price ?? row.package_price ?? row.catalog_price ?? 0,
          commission_percent: saved.commission_percent ?? row.commission_percent ?? 0,
        }
      })

      this.lines = next
      this.removedIds = Array.isArray(existing.removed)
        ? existing.removed.map(id => Number(id))
        : []
      this.stateReady = true
    },

    mergeRowsIntoState(rows) {
      if (! rows.length) {
        return
      }

      this.cachedRows = rows.map(row => this.normalizeRow(row))

      const next = { ...this.lines }

      this.cachedRows.forEach(row => {
        if (! next[row.course_id]) {
          next[row.course_id] = {
            price: row.package_price ?? row.catalog_price ?? 0,
            commission_percent: row.commission_percent ?? 0,
          }
        }
      })

      this.lines = next
    },

    parseValue(value) {
      if (! value) {
        return {}
      }

      if (typeof value === 'object') {
        return value
      }

      try {
        const parsed = JSON.parse(value)

        if (Array.isArray(parsed?.courses)) {
          const lines = {}
          parsed.courses.forEach(line => {
            lines[line.course_id] = {
              price: line.price ?? 0,
              commission_percent: line.commission_percent ?? 0,
            }
          })

          return { ...lines, removed: parsed.removed ?? [] }
        }

        return parsed || {}
      } catch (error) {
        return {}
      }
    },

    lineValue(courseId, key) {
      const id = Number(courseId)

      return this.lines[id]?.[key] ?? ''
    },

    numericPrice(courseId) {
      const id = Number(courseId)
      const value = Number(this.lines[id]?.price ?? 0)

      return Number.isFinite(value) && value >= 0 ? value : 0
    },

    isCommissionDisabled(courseId) {
      return this.numericPrice(courseId) === 0
    },

    formattedPriceInput(courseId) {
      const id = Number(courseId)
      const raw = this.lines[id]?.price

      if (raw === '' || raw === null || raw === undefined) {
        return ''
      }

      const num = Number(raw)

      if (! Number.isFinite(num)) {
        return ''
      }

      return numeral(num).format('0,0')
    },

    updatePriceFromInput(courseId, event) {
      const id = Number(courseId)
      const parsed = numeral(event.target.value).value()
      const price = parsed === null || Number.isNaN(parsed)
        ? 0
        : Math.max(0, Math.round(parsed))
      const next = {
        ...this.lines[id],
        price,
      }

      if (price === 0) {
        next.commission_percent = 0
      }

      this.lines = {
        ...this.lines,
        [id]: next,
      }
    },

    normalizeCommission(value) {
      if (value === '' || value === null || value === undefined) {
        return 0
      }

      const num = Number(value)

      if (! Number.isFinite(num)) {
        return 0
      }

      return Math.min(100, Math.max(0, Math.round(num)))
    },

    updateCommission(courseId, value) {
      const id = Number(courseId)

      if (this.isCommissionDisabled(id)) {
        return
      }

      this.lines = {
        ...this.lines,
        [id]: {
          ...this.lines[id],
          commission_percent: this.normalizeCommission(value),
        },
      }
    },

    clampCommission(courseId) {
      const id = Number(courseId)

      if (this.isCommissionDisabled(id)) {
        return
      }

      const current = this.lines[id]?.commission_percent

      if (current === '' || current === null || current === undefined) {
        return
      }

      this.lines = {
        ...this.lines,
        [id]: {
          ...this.lines[id],
          commission_percent: this.normalizeCommission(current),
        },
      }
    },

    removeRow(courseId) {
      const id = Number(courseId)

      if (! this.canRemoveRow(id)) {
        return
      }

      this.removedIds = [...this.removedIds, id]
    },

    formatMoney(amount) {
      return numeral(Number(amount || 0)).format('0,0')
    },

    formatPrice(amount) {
      return `${this.formatMoney(amount)} تومان`
    },

    fill(formData) {
      const courses = this.activeRows.map(row => ({
        course_id: row.course_id,
        price: this.numericPrice(row.course_id),
        commission_percent: this.isCommissionDisabled(row.course_id)
          ? 0
          : this.normalizeCommission(this.lines[row.course_id]?.commission_percent ?? 0),
      }))

      this.fillIfVisible(
        formData,
        this.fieldAttribute,
        JSON.stringify({ courses, removed: this.removedIds })
      )
    },

    onSyncedField() {
      const syncedRows = (this.currentField.rows || []).map(row => this.normalizeRow(row))

      if (syncedRows.length === 0) {
        return
      }

      if (! this.stateReady) {
        this.mergeRowsIntoState(syncedRows)
        this.bootstrapState()

        return
      }

      this.mergeRowsIntoState(syncedRows)
    },
  },
}
</script>

<style scoped>
.pct-root {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pct-list-scroll {
  max-height: 22rem;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid rgba(var(--colors-gray-200), 1);
  border-radius: 0.625rem;
  background: rgba(var(--colors-gray-50), 0.35);
  padding: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--colors-gray-300), 1) transparent;
}

.pct-list-scroll::-webkit-scrollbar {
  width: 6px;
}

.pct-list-scroll::-webkit-scrollbar-thumb {
  background: rgba(var(--colors-gray-300), 1);
  border-radius: 9999px;
}

html.dark .pct-list-scroll,
.dark .pct-list-scroll {
  background: rgba(var(--colors-gray-950), 0.35);
  border-color: rgba(var(--colors-gray-700), 1);
  scrollbar-color: rgba(var(--colors-gray-600), 1) transparent;
}

html.dark .pct-list-scroll::-webkit-scrollbar-thumb,
.dark .pct-list-scroll::-webkit-scrollbar-thumb {
  background: rgba(var(--colors-gray-600), 1);
}

.pct-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.pct-item {
  border: 1px solid rgba(var(--colors-gray-200), 1);
  border-radius: 0.625rem;
  background: rgba(var(--colors-white), 1);
  overflow: hidden;
}

html.dark .pct-item,
.dark .pct-item {
  background: rgba(var(--colors-gray-900), 1);
  border-color: rgba(var(--colors-gray-700), 1);
}

.pct-item-main {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0.75rem 0.625rem;
}

.pct-media {
  flex-shrink: 0;
}

.pct-cover {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.5rem;
  object-fit: cover;
  display: block;
  border: 1px solid rgba(var(--colors-gray-200), 1);
}

html.dark .pct-cover,
.dark .pct-cover {
  border-color: rgba(var(--colors-gray-700), 1);
}

.pct-cover-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--colors-gray-100), 1);
  color: rgba(var(--colors-gray-400), 1);
}

html.dark .pct-cover-fallback,
.dark .pct-cover-fallback {
  background: rgba(var(--colors-gray-800), 1);
  color: rgba(var(--colors-gray-500), 1);
}

.pct-cover-fallback svg {
  width: 1.25rem;
  height: 1.25rem;
}

.pct-info {
  flex: 1;
  min-width: 0;
}

.pct-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  color: rgba(var(--colors-gray-900), 1);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

html.dark .pct-title,
.dark .pct-title {
  color: rgba(var(--colors-gray-100), 1);
}

.pct-institute {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.375rem;
  min-width: 0;
}

.pct-institute-logo {
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 9999px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(var(--colors-gray-200), 1);
}

.pct-institute-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: rgba(var(--colors-primary-400), 1);
  flex-shrink: 0;
}

.pct-institute-name {
  font-size: 11px;
  line-height: 1.4;
  color: rgba(var(--colors-gray-500), 1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pct-remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: rgba(var(--colors-gray-400), 1);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.pct-remove svg {
  width: 0.9rem;
  height: 0.9rem;
}

.pct-remove:hover:not(:disabled) {
  color: rgba(var(--colors-red-600), 1);
  background: rgba(var(--colors-red-50), 1);
}

html.dark .pct-remove:hover:not(:disabled),
.dark .pct-remove:hover:not(:disabled) {
  color: rgba(var(--colors-red-400), 1);
  background: rgba(var(--colors-red-950), 0.5);
}

.pct-remove:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

.pct-item-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.625rem;
  padding: 0.625rem 0.75rem 0.75rem;
  border-top: 1px solid rgba(var(--colors-gray-100), 1);
  background: rgba(var(--colors-gray-50), 0.55);
}

html.dark .pct-item-fields,
.dark .pct-item-fields {
  border-color: rgba(var(--colors-gray-800), 1);
  background: rgba(var(--colors-gray-950), 0.35);
}

.pct-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}

.pct-field-sm {
  max-width: 6.5rem;
}

.pct-field-label {
  font-size: 10px;
  font-weight: 600;
  color: rgba(var(--colors-gray-500), 1);
  white-space: nowrap;
}

.pct-field-value {
  font-size: 13px;
  font-weight: 600;
  direction: ltr;
  text-align: left;
  font-variant-numeric: tabular-nums;
  color: rgba(var(--colors-gray-600), 1);
  padding-top: 0.35rem;
}

.pct-price-field,
.pct-commission-field {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pct-input {
  width: 100%;
  border: 1px solid rgba(var(--colors-gray-300), 1);
  border-radius: 0.375rem;
  padding: 0.4rem 0.5rem;
  font-size: 12px;
  background: rgba(var(--colors-white), 1);
  color: rgba(var(--colors-gray-900), 1);
  direction: ltr;
  text-align: left;
  font-variant-numeric: tabular-nums;
}

html.dark .pct-input,
.dark .pct-input {
  background: rgba(var(--colors-gray-900), 1);
  border-color: rgba(var(--colors-gray-600), 1);
  color: rgba(var(--colors-gray-100), 1);
}

.pct-input:focus {
  outline: none;
  border-color: rgba(var(--colors-primary-400), 1);
  box-shadow: 0 0 0 2px rgba(var(--colors-primary-500), 0.12);
}

.pct-input:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pct-input-sm {
  width: 3rem;
  flex-shrink: 0;
  text-align: center;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.pct-input-suffix,
.pct-percent-sign {
  flex-shrink: 0;
  font-size: 10px;
  color: rgba(var(--colors-gray-400), 1);
}

.pct-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  font-size: 13px;
  color: rgba(var(--colors-gray-400), 1);
  border: 1px dashed rgba(var(--colors-gray-300), 1);
  border-radius: 0.625rem;
}

.pct-summary {
  padding: 0.75rem;
  border: 1px solid rgba(var(--colors-gray-200), 1);
  border-radius: 0.625rem;
  background: rgba(var(--colors-gray-50), 0.8);
}

html.dark .pct-summary,
.dark .pct-summary {
  background: rgba(var(--colors-gray-800), 0.6);
  border-color: rgba(var(--colors-gray-700), 1);
}

.pct-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.pct-summary-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
}

.pct-summary-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(var(--colors-gray-500), 1);
  text-align: start;
  width: 100%;
}

.pct-summary-value {
  display: block;
  width: 100%;
  direction: ltr;
  text-align: end;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgba(var(--colors-gray-900), 1);
  unicode-bidi: isolate;
}

html.dark .pct-summary-value,
.dark .pct-summary-value {
  color: rgba(var(--colors-gray-100), 1);
}

.pct-summary-value-muted {
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--colors-gray-500), 1);
}

.pct-summary-over {
  background: rgba(var(--colors-red-50), 0.9);
  border-color: rgba(var(--colors-red-300), 1);
}

html.dark .pct-summary-over,
.dark .pct-summary-over {
  background: rgba(var(--colors-red-950), 0.35);
  border-color: rgba(var(--colors-red-800), 1);
}

.pct-summary-over .pct-summary-value {
  color: rgba(var(--colors-red-600), 1);
}

.pct-summary-error {
  margin-top: 0.5rem;
  padding: 0.5rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(var(--colors-red-700), 1);
  background: rgba(var(--colors-red-100), 0.8);
}

html.dark .pct-summary-error,
.dark .pct-summary-error {
  color: rgba(var(--colors-red-300), 1);
  background: rgba(var(--colors-red-950), 0.6);
}

@media (max-width: 640px) {
  .pct-item-fields {
    grid-template-columns: 1fr;
  }

  .pct-field-sm {
    max-width: none;
  }

  .pct-summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
